import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createExternalRegistryUser } from '../../api/Individual';
import { searchPatient } from '../api/Patient';
import {
  listRegistryRequests,
  updateRegistryRequest,
} from '../../api/FamilyPracticeRegistry';
import ScheduleAppointmentWR from '../Queue/ScheduleAppointmentWR';
import './NFPRCrmDashboard.css';

const STATUS_OPTIONS = [
  { value: 'arrived', label: 'Submitted' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'follow-up', label: 'Follow-up required' },
  { value: 'validated', label: 'Validated' },
  { value: 'patient-linked', label: 'Patient linked' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITY_LABELS = {
  1: 'Urgent',
  2: 'High',
  3: 'Medium',
  4: 'Normal',
  5: 'Routine',
};

const buildMockRequest = ({
  id,
  confirmationNumber,
  display,
  birthDate,
  gender,
  hcn,
  phone,
  email,
  community,
  postalCode,
  addressLine1,
  addressLine2,
  priority = 5,
  status = 'arrived',
  preferredChannel = 'phone',
  preferredLanguage = 'English',
  familyMemberCount = 0,
  healthNeeds = [],
  additionalInformation = '',
  submittedAt,
}) => ({
  id,
  orgId: 'nova-scotia-811',
  status,
  priority,
  patientLocation: community,
  period: { start: submittedAt, end: null },
  createdAt: new Date(submittedAt).getTime(),
  subject: {
    display,
    reference: `Patient/${id}`,
    type: 'Patient',
    birthDate,
    gender,
    identifier: [
      {
        type: 'HCN',
        system: 'NS-MSI',
        value: hcn,
      },
    ],
  },
  intakeQuestions: {
    intakePriorityReason: 'Need a Family Practice Registry',
    intakeReason: 'Public Need a Family Practice Registry intake submitted online',
    reason: 'Patient is registering or updating information for the Need a Family Practice Registry.',
    preferredChannel,
    callerName: display,
    callerRelationship: 'Self',
    callerNumber: phone,
    callerNumberExt: '',
    email,
    preferredLanguage,
    confirmationNumber,
    addressLine1,
    addressLine2,
    community,
    province: 'NS',
    postalCode,
    addressValidated: true,
    addressSource: 'Canada Post AddressComplete',
    currentlyHasFamilyPracticeProvider: false,
    hasNovaScotiaHealthCard: true,
    livesInNovaScotia: true,
    registeringForSelf: true,
    familyMemberCount,
    healthNeedsSummary: healthNeeds.join('; ') || 'No additional health needs indicated',
    additionalInformation,
    consentCaptured: true,
    consentVersion: 'NFPR-PUBLIC-INTAKE-CONSENT-V1',
    submittedSource: 'Public Web Intake',
  },
  notes: [
    {
      note: `Public Need a Family Practice Registry intake submitted. Confirmation #: ${confirmationNumber}. Preferred contact: ${preferredChannel}. Health needs: ${healthNeeds.join(', ') || 'none indicated'}. Family members included: ${familyMemberCount}.`,
      actor: {
        display: 'Public Web Intake',
        reference: 'System/public-intake',
        type: 'System',
      },
      updatedAt: new Date(submittedAt).getTime(),
    },
  ],
  practitionerHistory: [],
  linkedPatient: null,
  actionHistory: [],
  priorityHistory: [
    {
      actor: {
        display: 'Public Web Intake',
        reference: 'System/public-intake',
        type: 'System',
      },
      oldPriority: null,
      newPriority: priority,
      updatedAt: new Date(submittedAt).getTime(),
    },
  ],
});

const MOCK_REQUESTS = [
  buildMockRequest({
    id: 'nfpr-000482',
    confirmationNumber: 'NFPR-2026-000482',
    display: 'Sarah MacDonald',
    birthDate: '1984-04-12',
    gender: 'female',
    hcn: '1234567897',
    phone: '9025550148',
    email: 'sarah.macdonald@example.ca',
    community: 'Dartmouth',
    postalCode: 'B2Y 2N6',
    addressLine1: '25 Alderney Drive',
    addressLine2: 'Unit 302',
    priority: 5,
    status: 'arrived',
    preferredChannel: 'phone',
    familyMemberCount: 1,
    healthNeeds: ['Ongoing medication needs', 'Chronic condition', 'Difficulty travelling for care'],
    additionalInformation: 'Patient reports ongoing medication renewal needs and difficulty travelling outside the local community.',
    submittedAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
  }),
  buildMockRequest({
    id: 'nfpr-000481',
    confirmationNumber: 'NFPR-2026-000481',
    display: 'Michael Boudreau',
    birthDate: '1957-11-03',
    gender: 'male',
    hcn: '2345678901',
    phone: '9025550199',
    email: 'michael.boudreau@example.ca',
    community: 'Sydney',
    postalCode: 'B1P 1J3',
    addressLine1: '275 Charlotte Street',
    addressLine2: '',
    priority: 3,
    status: 'needs-review',
    preferredChannel: 'phone',
    familyMemberCount: 0,
    healthNeeds: ['Chronic condition', 'Mobility or accessibility needs'],
    additionalInformation: 'Patient indicates limited mobility and prefers a practice close to home.',
    submittedAt: new Date(Date.now() - 1000 * 60 * 64).toISOString(),
  }),
  buildMockRequest({
    id: 'nfpr-000480',
    confirmationNumber: 'NFPR-2026-000480',
    display: 'Ava Williams',
    birthDate: '1992-08-27',
    gender: 'female',
    hcn: '3456789012',
    phone: '9025550112',
    email: 'ava.williams@example.ca',
    community: 'Halifax',
    postalCode: 'B3J 1M7',
    addressLine1: '5161 George Street',
    addressLine2: '',
    priority: 4,
    status: 'follow-up',
    preferredChannel: 'email',
    preferredLanguage: 'English',
    familyMemberCount: 2,
    healthNeeds: ['Needs help accessing virtual care'],
    additionalInformation: 'Patient requested help updating information for children included on the request.',
    submittedAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
  }),
];

const getStatusLabel = (status) => STATUS_OPTIONS.find((option) => option.value === status)?.label || status;

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

const normalizePublicIntakePayload = (payload, index) => {
  if (!payload) return null;
  const patient = payload.patient || payload.subject || {};
  const meta = payload.metadata || payload.intakeQuestions || {};
  const confirmationNumber = meta.confirmationNumber || payload.confirmationNumber || payload.confirmation?.confirmationNumber || `NFPR-2026-LOCAL-${index + 1}`;
  const display = patient.display || [patient.firstName, patient.lastName].filter(Boolean).join(' ') || 'Public intake patient';
  const submittedAt = payload.submittedAt || payload.createdAt || payload.period?.start || new Date().toISOString();

  return {
    id: payload.id || `local-${index}`,
    orgId: payload.orgId || 'nova-scotia-811',
    status: payload.status || 'arrived',
    priority: Number(payload.priority || 5),
    patientLocation: patient.community || meta.community || patient.address?.community || 'Nova Scotia',
    period: { start: submittedAt, end: null },
    createdAt: new Date(submittedAt).getTime(),
    subject: {
      display,
      reference: patient.reference || `Patient/local-${index}`,
      type: 'Patient',
      birthDate: patient.birthDate || patient.dateOfBirth || '',
      gender: patient.gender || patient.sexAtBirth || '',
      identifier: [
        {
          type: 'HCN',
          system: 'NS-MSI',
          value: patient.healthCardNumber || patient.healthCard?.number || '',
        },
      ],
    },
    intakeQuestions: {
      intakePriorityReason: 'Need a Family Practice Registry',
      intakeReason: 'Public Need a Family Practice Registry intake submitted online',
      reason: meta.reason || 'Patient is registering or updating information for the Need a Family Practice Registry.',
      preferredChannel: meta.preferredContactMethod || payload.preferredContactMethod || patient.preferredContactMethod || 'phone',
      callerName: display,
      callerRelationship: 'Self',
      callerNumber: patient.phone || payload.phone || meta.callerNumber || '',
      email: patient.email || payload.email || meta.email || '',
      preferredLanguage: patient.preferredLanguage || payload.preferredLanguage || meta.preferredLanguage || 'English',
      confirmationNumber,
      addressLine1: patient.address?.streetAddress || meta.addressLine1 || '',
      addressLine2: patient.address?.unit || meta.addressLine2 || '',
      community: patient.address?.community || meta.community || '',
      province: 'NS',
      postalCode: patient.address?.postalCode || meta.postalCode || '',
      addressValidated: Boolean(patient.address?.addressValidated || meta.addressValidated),
      addressSource: patient.address?.addressSource || meta.addressSource || 'Canada Post AddressComplete',
      familyMemberCount: Number(meta.familyMemberCount || payload.familyMemberCount || 0),
      healthNeedsSummary: Array.isArray(meta.healthNeedsFlags) ? meta.healthNeedsFlags.join('; ') : meta.healthNeedsSummary || 'Health needs captured in intake form',
      additionalInformation: meta.additionalInformation || '',
      consentCaptured: meta.consentCaptured ?? true,
      consentVersion: meta.consentVersion || 'NFPR-PUBLIC-INTAKE-CONSENT-V1',
      submittedSource: 'Public Web Intake',
    },
    notes: payload.notes || [
      {
        note: `Public Need a Family Practice Registry intake submitted. Confirmation #: ${confirmationNumber}.`,
        actor: { display: 'Public Web Intake', reference: 'System/public-intake', type: 'System' },
        updatedAt: new Date(submittedAt).getTime(),
      },
    ],
    linkedPatient: meta.linkedPatient || null,
    actionHistory: meta.actionHistory || [],
    practitionerHistory: [],
    priorityHistory: [],
  };
};

const loadLocalPublicIntakes = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('nfprPublicIntakeQueue') || '[]');
    if (!Array.isArray(stored)) return [];
    return stored.map(normalizePublicIntakePayload).filter(Boolean);
  } catch (error) {
    return [];
  }
};

const loadFallbackRequests = () => {
  const localRequests = loadLocalPublicIntakes();
  return localRequests.length ? [...localRequests, ...MOCK_REQUESTS] : MOCK_REQUESTS;
};

const getRegistryActorName = (user) => user?.name?.[0]?.text || user?.email || 'Registry Support User';

const buildPatientSearchDefaults = (request) => {
  const display = request?.subject?.display || '';
  const nameParts = display.trim().split(/\s+/).filter(Boolean);
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : (nameParts[0] || '');
  const healthCardNumber = request?.subject?.identifier?.find((identifier) => identifier?.type === 'HCN')?.value
    || request?.subject?.identifier?.[0]?.value
    || '';

  return {
    firstName,
    lastName,
    birthDate: request?.subject?.birthDate || '',
    healthCardNumber,
  };
};

const getPatientIdFromResponse = (response) => {
  const data = response?.data || response?.result || response;
  return data?.id
    || data?.patientId
    || data?.link?.find((link) => link?.target?.type === 'Patient')?.target?.reference?.split('/')?.[1]
    || data?.data?.id
    || data?.data?.link?.find((link) => link?.target?.type === 'Patient')?.target?.reference?.split('/')?.[1]
    || null;
};

const getPatientId = (patient) => patient?.id
  || patient?.patientId
  || patient?.link?.find((link) => link?.target?.type === 'Patient')?.target?.reference?.split('/')?.[1]
  || patient?.reference?.split('/')?.[1]
  || null;

const getPatientDisplayName = (patient) => patient?.name?.[0]?.text
  || patient?.display
  || [patient?.firstName, patient?.lastName].filter(Boolean).join(' ')
  || 'Patient record';

const getPatientHcn = (patient) => patient?.identifier?.find((identifier) => identifier?.type === 'HCN')?.value
  || patient?.identifier?.[0]?.value
  || patient?.healthCardNumber
  || '';

const buildLinkedPatientFromRecord = (patient) => {
  const id = getPatientId(patient);
  return {
    id,
    reference: id ? `Patient/${id}` : patient?.reference || '',
    display: getPatientDisplayName(patient),
    birthDate: patient?.birthDate || '',
    hcn: getPatientHcn(patient),
    source: patient?.source || 'Florizel Patient Record',
    linkedAt: new Date().toISOString(),
  };
};

const buildPatientCreatePayload = (request) => {
  const display = request?.subject?.display || '';
  const nameParts = display.trim().split(/\s+/);
  const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0] || '';
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
  const hcn = request?.subject?.identifier?.[0]?.value || '';
  const email = request?.intakeQuestions?.email || '';
  const phone = request?.intakeQuestions?.callerNumber || '';

  return {
    type: 'Patient',
    name: [
      {
        use: 'usual',
        text: display,
        family: lastName,
        given: [firstName].filter(Boolean),
      },
    ],
    gender: request?.subject?.gender || '',
    birthDate: request?.subject?.birthDate || '',
    telecom: [
      { system: 'phone', value: phone, use: 'home' },
      { system: 'email', value: email, use: 'home' },
    ].filter((item) => item.value),
    address: [
      {
        use: 'home',
        line: [request?.intakeQuestions?.addressLine1, request?.intakeQuestions?.addressLine2].filter(Boolean),
        city: request?.intakeQuestions?.community || '',
        state: request?.intakeQuestions?.province || 'NS',
        postalCode: request?.intakeQuestions?.postalCode || '',
        country: 'CA',
      },
    ],
    identifier: hcn ? [
      {
        type: 'HCN',
        system: 'NS-MSI',
        value: hcn,
      },
    ] : [],
    healthCardNumber: hcn,
    healthCardAuthority: 'NS',
    email,
    createAccount: false,
    source: 'REGISTRY',
  };
};

const StatCard = ({ label, value, helper }) => (
  <div className="crm-stat-card">
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{helper}</small>
  </div>
);

const NFPRCrmDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth?.user);
  const organizations = user?.organizations || [];
  const primaryOrgId = organizations?.[0]?.id;

  const [requests, setRequests] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const [patientSearchFields, setPatientSearchFields] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    healthCardNumber: '',
  });
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const [patientActionMessage, setPatientActionMessage] = useState('');
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [usingFallbackData, setUsingFallbackData] = useState(false);
  const [actionError, setActionError] = useState('');

  const applyRequests = useCallback((items, isFallback) => {
    setRequests(items);
    setUsingFallbackData(isFallback);
    setActiveId((current) => {
      if (current && items.some((request) => request.id === current)) return current;
      return items[0]?.id || null;
    });
  }, []);

  const fetchRegistryRequests = useCallback(async () => {
    if (!primaryOrgId) {
      setLoadError('Unable to load registry requests because no organization is available for the current user.');
      applyRequests(loadFallbackRequests(), true);
      setIsLoadingRequests(false);
      return;
    }

    setIsLoadingRequests(true);
    setLoadError('');

    try {
      const response = await listRegistryRequests({ orgId: primaryOrgId, limit: 50 });
      const items = Array.isArray(response?.data) ? response.data : [];

      if (items.length > 0) {
        applyRequests(items, false);
        setUsingFallbackData(false);
        return;
      }

      applyRequests(loadFallbackRequests(), true);
      setLoadError('No live registry requests found for your organization. Showing demo data.');
    } catch (error) {
      applyRequests(loadFallbackRequests(), true);
      setLoadError(
        error?.message
        || error?.error
        || error?.response?.data?.message
        || error?.response?.data?.error
        || 'Unable to load registry requests. Showing demo data.'
      );
    } finally {
      setIsLoadingRequests(false);
    }
  }, [applyRequests, primaryOrgId]);

  useEffect(() => {
    fetchRegistryRequests();
  }, [fetchRegistryRequests]);

  const replaceRequestInState = useCallback((updated) => {
    if (!updated?.id) return;
    setRequests((previous) => previous.map((request) => (
      request.id === updated.id ? updated : request
    )));
  }, []);

  const filteredRequests = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests
      .filter((request) => statusFilter === 'all' || request.status === statusFilter)
      .filter((request) => {
        if (!query) return true;
        const searchable = [
          request.subject?.display,
          request.intakeQuestions?.confirmationNumber,
          request.patientLocation,
          request.intakeQuestions?.postalCode,
          request.subject?.identifier?.[0]?.value,
        ].join(' ').toLowerCase();
        return searchable.includes(query);
      })
      .sort((a, b) => a.priority - b.priority || b.createdAt - a.createdAt);
  }, [requests, search, statusFilter]);

  const activeRequest = requests.find((request) => request.id === activeId) || filteredRequests[0] || requests[0];
  const stats = useMemo(() => ({
    total: requests.length,
    submitted: requests.filter((request) => request.status === 'arrived').length,
    followUp: requests.filter((request) => request.status === 'follow-up' || request.status === 'needs-review').length,
    familyMembers: requests.reduce((sum, request) => sum + Number(request.intakeQuestions?.familyMemberCount || 0), 0),
  }), [requests]);

  const updateStatus = async (id, status) => {
    const request = requests.find((item) => item.id === id);
    if (!request || request.status === status) return;

    const label = getStatusLabel(status);
    const details = `Request status changed to ${label}`;

    if (usingFallbackData) {
      setActionError('');
      setRequests((previous) => previous.map((item) => {
        if (item.id !== id) return item;
        return appendActionHistory({ ...item, status }, 'Status changed', details);
      }));
      return;
    }

    setActionError('');
    try {
      const response = await updateRegistryRequest(id, { status, details });
      replaceRequestInState(response?.data);
    } catch (error) {
      setActionError(
        error?.message
        || error?.error
        || error?.response?.data?.message
        || error?.response?.data?.error
        || 'Unable to update request status.'
      );
    }
  };

  const addNote = async () => {
    const note = noteDraft.trim();
    if (!note || !activeRequest) return;

    const updatedAt = Date.now();
    const actor = {
      display: getRegistryActorName(user),
      reference: 'Practitioner/crm-user',
      type: 'Practitioner',
    };
    const nextNotes = [
      { note, actor, updatedAt },
      ...(activeRequest.notes || []),
    ];

    if (usingFallbackData) {
      setActionError('');
      setRequests((previous) => previous.map((item) => {
        if (item.id !== activeRequest.id) return item;
        return { ...item, notes: nextNotes };
      }));
      setNoteDraft('');
      return;
    }

    setActionError('');
    try {
      const response = await updateRegistryRequest(activeRequest.id, {
        notes: nextNotes,
        details: 'Note added',
      });
      replaceRequestInState(response?.data);
      setNoteDraft('');
    } catch (error) {
      setActionError(
        error?.message
        || error?.error
        || error?.response?.data?.message
        || error?.response?.data?.error
        || 'Unable to save note.'
      );
    }
  };

  const appendActionHistory = (request, action, details = '') => ({
    ...request,
    actionHistory: [
      {
        action,
        details,
        actor: getRegistryActorName(user),
        updatedAt: Date.now(),
      },
      ...(request.actionHistory || []),
    ],
  });

  const linkPatientToRequest = async (patient, source = 'linked') => {
    if (!activeRequest || !patient) return;

    const linkedPatient = buildLinkedPatientFromRecord(patient);
    const details = `Patient linked: ${linkedPatient.display}${linkedPatient.reference ? ` (${linkedPatient.reference})` : ''}`;

    if (usingFallbackData) {
      setActionError('');
      setRequests((previous) => previous.map((item) => {
        if (item.id !== activeRequest.id) return item;
        return appendActionHistory({
          ...item,
          status: 'patient-linked',
          subject: {
            ...item.subject,
            reference: linkedPatient.reference || item.subject?.reference,
          },
          linkedPatient: {
            ...linkedPatient,
            source,
          },
        }, 'Patient linked', details);
      }));
      setPatientActionMessage(`Linked to ${linkedPatient.display}${linkedPatient.reference ? ` (${linkedPatient.reference})` : ''}.`);
      setPatientSearchOpen(false);
      return;
    }

    setActionError('');
    try {
      const response = await updateRegistryRequest(activeRequest.id, {
        linkedPatient: {
          ...linkedPatient,
          source,
        },
        status: 'patient-linked',
        details,
      });
      replaceRequestInState(response?.data);
      setPatientActionMessage(`Linked to ${linkedPatient.display}${linkedPatient.reference ? ` (${linkedPatient.reference})` : ''}.`);
      setPatientSearchOpen(false);
    } catch (error) {
      setPatientActionMessage(
        error?.message
        || error?.error
        || error?.response?.data?.message
        || error?.response?.data?.error
        || 'Unable to link patient to registry request.'
      );
    }
  };

  const openPatientSearch = () => {
    setPatientSearchFields(buildPatientSearchDefaults(activeRequest));
    setPatientSearchResults([]);
    setPatientActionMessage('');
    setPatientSearchOpen(true);
  };

  const updatePatientSearchField = (field) => (event) => {
    const value = event.target.value;
    setPatientSearchFields((current) => ({ ...current, [field]: value }));
  };

  const searchExistingPatients = () => {
    if (!primaryOrgId) {
      setPatientActionMessage('Unable to search patients because no organization is available for the current user.');
      return;
    }

    const firstName = patientSearchFields.firstName?.trim() || '';
    const lastName = patientSearchFields.lastName?.trim() || '';
    const birthDate = patientSearchFields.birthDate?.trim() || '';
    const healthCardNumber = patientSearchFields.healthCardNumber?.trim() || '';

    if (!firstName && !lastName && !birthDate && !healthCardNumber) {
      setPatientActionMessage('Enter at least one search field: first name, last name, date of birth, or health card number.');
      return;
    }

    setIsSearchingPatients(true);
    setPatientActionMessage('');

    searchPatient({
      org_id: primaryOrgId,
      firstName,
      lastName,
      birthDate,
      healthCardNumber,
    })
      .then((response) => {
        if (response?.errors?.length) {
          const errorMessage = response.errors
            .map((error) => error.message || error.error)
            .filter(Boolean)
            .join(' ');
          if (errorMessage) {
            setPatientActionMessage(errorMessage);
          }
        }

        const results = Array.isArray(response?.result) ? response.result : [];
        setPatientSearchResults(results);
        if (!results.length) {
          setPatientActionMessage('No matching patient records found.');
        }
      })
      .catch((error) => {
        setPatientActionMessage(
          error?.response?.data?.message
          || error?.response?.data?.error
          || error?.message
          || 'Unable to search patient records.'
        );
      })
      .finally(() => setIsSearchingPatients(false));
  };

  const createPatientRecord = () => {
    if (!activeRequest || !primaryOrgId) {
      setPatientActionMessage('Unable to create a patient record because no organization is available for the current user.');
      return;
    }

    const confirmed = window.confirm('Create a Florizel patient record from this reviewed registry request? The registry request will remain separate and linked to the new patient record.');
    if (!confirmed) return;

    setIsCreatingPatient(true);
    setPatientActionMessage('');
    const payload = buildPatientCreatePayload(activeRequest);
    createExternalRegistryUser(payload, primaryOrgId, organizations)
      .then((response) => {
        const patientId = getPatientIdFromResponse(response);
        const linkedPatient = {
          id: patientId,
          reference: patientId ? `Patient/${patientId}` : '',
          display: payload.name?.[0]?.text || activeRequest.subject.display,
          birthDate: payload.birthDate,
          hcn: payload.identifier?.[0]?.value || '',
          source: 'Created from registry request',
          linkedAt: new Date().toISOString(),
        };
        linkPatientToRequest(linkedPatient, 'Created from registry request');
        setPatientActionMessage(`Patient record created and linked${patientId ? ` (${linkedPatient.reference})` : ''}.`);
      })
      .catch((error) => {
        setPatientActionMessage(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unable to create patient record.');
      })
      .finally(() => setIsCreatingPatient(false));
  };

  const viewLinkedPatientRecord = () => {
    const patientId = activeRequest?.linkedPatient?.id || activeRequest?.linkedPatient?.reference?.split('/')?.[1];
    if (!patientId) return;
    navigate('/app/patients-details', {
      state: {
        id: patientId,
        name: activeRequest?.linkedPatient?.display || activeRequest?.subject?.display,
        isDoctor: false,
      },
    });
  };

  const openAppointmentScheduler = () => {
    if (!activeRequest?.linkedPatient) return;
    setRequests((previous) => previous.map((request) => {
      if (request.id !== activeRequest.id) return request;
      return appendActionHistory(request, 'Appointment scheduling opened', 'Registry support opened the appointment scheduling workflow for the linked patient.');
    }));
    setAppointmentModalOpen(true);
  };

  const getLinkedPatientIdForScheduling = () => activeRequest?.linkedPatient?.id
    || activeRequest?.linkedPatient?.reference?.split('/')?.[1]
    || activeRequest?.subject?.reference?.split('/')?.[1]
    || '';

  return (
    <main className="crm-page-shell" aria-label="Care Access Registry workspace">
      {usingFallbackData && (
        <div className="crm-fallback-banner" role="status">
          Showing demo data — live registry unavailable{loadError ? `: ${loadError}` : ''}.
        </div>
      )}

      {!usingFallbackData && loadError && (
        <div className="crm-info-banner" role="status">{loadError}</div>
      )}

      <section className="crm-stat-grid" aria-label="Registry request summary">
        <StatCard label="Total requests" value={stats.total} helper="Registry intake records" />
        <StatCard label="Submitted" value={stats.submitted} helper="Ready for first review" />
        <StatCard label="Needs attention" value={stats.followUp} helper="Review or follow-up required" />
        <StatCard label="Family members" value={stats.familyMembers} helper="Additional household members" />
      </section>

      <section className="crm-workspace">
        <div className="crm-list-panel">
          <div className="crm-toolbar">
            <div>
              <h2>Registry requests</h2>
              <p>Sorted by priority and submission time.</p>
            </div>
            <button
              type="button"
              className="crm-secondary-button"
              onClick={fetchRegistryRequests}
              disabled={isLoadingRequests}
            >
              {isLoadingRequests ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          <div className="crm-filter-row">
            <label>
              <span>Search</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, HCN, postal code, confirmation #"
              />
            </label>
            <label>
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="crm-request-list" role="list" aria-label="Registry intake requests">
            {isLoadingRequests && requests.length === 0 ? (
              <p className="crm-loading-message">Loading registry requests…</p>
            ) : null}
            {!isLoadingRequests && filteredRequests.length === 0 ? (
              <p className="crm-loading-message">No registry requests match the current filters.</p>
            ) : null}
            {filteredRequests.map((request) => (
              <button
                key={request.id}
                type="button"
                className={`crm-request-card ${activeRequest?.id === request.id ? 'is-active' : ''}`}
                onClick={() => setActiveId(request.id)}
              >
                <div className="crm-card-topline">
                  <strong>{request.subject.display}</strong>
                  <span className={`crm-status-pill status-${request.status}`}>{getStatusLabel(request.status)}</span>
                </div>
                <div className="crm-card-meta">
                  <span>{request.intakeQuestions.confirmationNumber}</span>
                  <span>{request.patientLocation}</span>
                  <span>{formatDateTime(request.period.start)}</span>
                </div>
                <div className="crm-card-bottomline">
                  <span>Priority: {PRIORITY_LABELS[request.priority] || request.priority}</span>
                  <span>{request.intakeQuestions.healthNeedsSummary}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {activeRequest && (
          <aside className="crm-detail-panel" aria-label="Selected registry request details">
            <div className="crm-detail-header">
              <div>
                <span className="crm-eyebrow">Selected request</span>
                <h2>{activeRequest.subject.display}</h2>
                <p>{activeRequest.intakeQuestions.confirmationNumber}</p>
              </div>
              <span className={`crm-status-pill status-${activeRequest.status}`}>{getStatusLabel(activeRequest.status)}</span>
            </div>

            <section className="crm-status-card" aria-label="Request status management">
              {actionError && <p className="crm-action-message crm-action-error">{actionError}</p>}
              <div>
                <span className="crm-eyebrow">Request status</span>
                <h3>{getStatusLabel(activeRequest.status)}</h3>
                <p>Update the registry request status as the request is reviewed, validated, linked to a patient record, or closed.</p>
              </div>
              <label className="crm-status-select">
                <span>Change status</span>
                <select
                  value={activeRequest.status}
                  onChange={(event) => updateStatus(activeRequest.id, event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="crm-action-row" aria-label="Quick status actions">
                <button type="button" onClick={() => updateStatus(activeRequest.id, 'needs-review')}>Needs review</button>
                <button type="button" onClick={() => updateStatus(activeRequest.id, 'in-progress')}>Start review</button>
                <button type="button" onClick={() => updateStatus(activeRequest.id, 'follow-up')}>Needs follow-up</button>
                <button type="button" onClick={() => updateStatus(activeRequest.id, 'validated')}>Validate</button>
                <button type="button" onClick={() => updateStatus(activeRequest.id, 'closed')}>Close request</button>
              </div>
            </section>

            <section className="crm-patient-link-card" aria-label="Patient record linkage">
              <div>
                <span className="crm-eyebrow">Patient record status</span>
                {activeRequest.linkedPatient ? (
                  <>
                    <h3>Linked patient record</h3>
                    <p>
                      <strong>{activeRequest.linkedPatient.display}</strong>
                      {activeRequest.linkedPatient.reference ? ` · ${activeRequest.linkedPatient.reference}` : ''}
                    </p>
                    <small>This registry request remains separate and is linked to the patient record for audit and follow-up.</small>
                  </>
                ) : (
                  <>
                    <h3>No linked patient record</h3>
                    <p>Review the request, search for an existing patient, or create a new Florizel patient record from the validated registry information.</p>
                  </>
                )}
              </div>
              <div className="crm-patient-actions">
                <button type="button" onClick={openPatientSearch}>Search existing patient</button>
                <button type="button" onClick={createPatientRecord} disabled={isCreatingPatient || Boolean(activeRequest.linkedPatient)}>
                  {isCreatingPatient ? 'Creating…' : 'Create patient record'}
                </button>
                {activeRequest.linkedPatient && (
                  <>
                    <button type="button" onClick={viewLinkedPatientRecord}>View patient record</button>
                    <button type="button" className="crm-primary-action" onClick={openAppointmentScheduler}>Schedule appointment</button>
                  </>
                )}
              </div>
              {patientActionMessage && <p className="crm-action-message">{patientActionMessage}</p>}
            </section>

            <div className="crm-detail-grid">
              <div>
                <span>Date of birth</span>
                <strong>{activeRequest.subject.birthDate || '—'}</strong>
              </div>
              <div>
                <span>Health card</span>
                <strong>{activeRequest.subject.identifier?.[0]?.value || '—'}</strong>
              </div>
              <div>
                <span>Preferred contact</span>
                <strong>{activeRequest.intakeQuestions.preferredChannel}</strong>
              </div>
              <div>
                <span>Phone</span>
                <strong>{activeRequest.intakeQuestions.callerNumber || '—'}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{activeRequest.intakeQuestions.email || '—'}</strong>
              </div>
              <div>
                <span>Family members</span>
                <strong>{activeRequest.intakeQuestions.familyMemberCount}</strong>
              </div>
            </div>

            <section className="crm-detail-section">
              <h3>Address</h3>
              <p>
                {activeRequest.intakeQuestions.addressLine1}
                {activeRequest.intakeQuestions.addressLine2 ? `, ${activeRequest.intakeQuestions.addressLine2}` : ''}<br />
                {activeRequest.intakeQuestions.community}, {activeRequest.intakeQuestions.province} {activeRequest.intakeQuestions.postalCode}
              </p>
              <small>{activeRequest.intakeQuestions.addressValidated ? `Validated by ${activeRequest.intakeQuestions.addressSource}` : 'Address not validated'}</small>
            </section>

            <section className="crm-detail-section">
              <h3>Health needs</h3>
              <p>{activeRequest.intakeQuestions.healthNeedsSummary}</p>
              {activeRequest.intakeQuestions.additionalInformation && <small>{activeRequest.intakeQuestions.additionalInformation}</small>}
            </section>

            <section className="crm-detail-section">
              <h3>Consent</h3>
              <p>{activeRequest.intakeQuestions.consentCaptured ? 'Consent captured through the public intake form.' : 'Consent requires review.'}</p>
              <small>{activeRequest.intakeQuestions.consentVersion}</small>
            </section>

            {activeRequest.actionHistory?.length > 0 && (
              <section className="crm-detail-section">
                <h3>Request actions</h3>
                <div className="crm-notes-list">
                  {activeRequest.actionHistory.map((action, index) => (
                    <article key={`${action.updatedAt}-${index}`}>
                      <p>{action.action}{action.details ? `: ${action.details}` : ''}</p>
                      <small>{action.actor || 'Registry Support User'} · {formatDateTime(action.updatedAt)}</small>
                    </article>
                  ))}
                </div>
              </section>
            )}

            <section className="crm-detail-section">
              <h3>Notes</h3>
              <label className="crm-note-box">
                <span>Add note</span>
                <textarea value={noteDraft} onChange={(event) => setNoteDraft(event.target.value)} rows="3" placeholder="Document validation, call attempt, or follow-up outcome" />
              </label>
              <button type="button" className="crm-primary-button" onClick={addNote}>Save note</button>
              <div className="crm-notes-list">
                {(activeRequest.notes || []).map((note, index) => (
                  <article key={`${note.updatedAt}-${index}`}>
                    <p>{note.note}</p>
                    <small>{note.actor?.display || 'User'} · {formatDateTime(note.updatedAt)}</small>
                  </article>
                ))}
              </div>
            </section>
          </aside>
        )}
      </section>

      {appointmentModalOpen && activeRequest?.linkedPatient && (
        <ScheduleAppointmentWR
          onShow={appointmentModalOpen}
          onHide={() => setAppointmentModalOpen(false)}
          questionnaireData={{
            callerRelationship: 'Self',
            callerNumber: activeRequest?.intakeQuestions?.callerNumber || '',
            callerNumberExt: activeRequest?.intakeQuestions?.callerNumberExt || '',
            intakeReason: 'Family practice registry follow-up appointment',
            howToProcced: 'SCHEDULE',
          }}
          patientName={activeRequest?.linkedPatient?.display || activeRequest?.subject?.display}
          patientId={getLinkedPatientIdForScheduling()}
          type="patient"
          orgId={primaryOrgId}
          patientLocation={activeRequest?.patientLocation || activeRequest?.intakeQuestions?.community || ''}
        />
      )}

      {patientSearchOpen && (
        <div className="crm-modal-backdrop" role="dialog" aria-modal="true" aria-label="Search existing patient records">
          <div className="crm-modal">
            <div className="crm-modal-header">
              <div>
                <span className="crm-eyebrow">Patient record lookup</span>
                <h2>Search existing patients</h2>
              </div>
              <button type="button" className="crm-icon-button" onClick={() => setPatientSearchOpen(false)} aria-label="Close patient search">×</button>
            </div>
            <p className="crm-modal-intro">Search before creating a patient record to avoid duplicates. Use the same patient search fields as Patient Management.</p>
            <div className="crm-patient-search-form">
              <label>
                <span>First name</span>
                <input
                  type="text"
                  value={patientSearchFields.firstName}
                  onChange={updatePatientSearchField('firstName')}
                  placeholder="First name"
                />
              </label>
              <label>
                <span>Last name</span>
                <input
                  type="text"
                  value={patientSearchFields.lastName}
                  onChange={updatePatientSearchField('lastName')}
                  placeholder="Last name"
                />
              </label>
              <label>
                <span>Date of birth</span>
                <input
                  type="date"
                  value={patientSearchFields.birthDate}
                  onChange={updatePatientSearchField('birthDate')}
                />
              </label>
              <label>
                <span>Health card number</span>
                <input
                  type="text"
                  value={patientSearchFields.healthCardNumber}
                  onChange={updatePatientSearchField('healthCardNumber')}
                  placeholder="Health card number"
                />
              </label>
            </div>
            <div className="crm-modal-search-row">
              <button type="button" onClick={searchExistingPatients} disabled={isSearchingPatients}>
                {isSearchingPatients ? 'Searching…' : 'Search'}
              </button>
            </div>
            {patientActionMessage && <p className="crm-action-message">{patientActionMessage}</p>}
            <div className="crm-patient-results">
              {patientSearchResults.map((patient, index) => {
                const patientId = getPatientId(patient);
                return (
                  <article key={patientId || index} className="crm-patient-result-card">
                    <div>
                      <h3>{getPatientDisplayName(patient)}</h3>
                      <p>
                        DOB: {patient.birthDate || '—'} · HCN: {getPatientHcn(patient) || '—'}
                      </p>
                      <small>{patientId ? `Patient/${patientId}` : 'Patient record'}</small>
                    </div>
                    <button type="button" onClick={() => linkPatientToRequest(patient, 'Existing Florizel patient')}>Link patient</button>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default NFPRCrmDashboard;
